import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type CoordinatesJSON = z.infer<typeof CoordinatesSchema.CoordinatesJSONZ>;
export type CoordinatesParams = z.infer<typeof CoordinatesSchema.CoordinatesZ>;

export default class CoordinatesSchema {
  public static readonly CoordinatesJSONZ = z.object({
    latitude: z.string(),
    longitude: z.string(),
  });

  public static readonly CoordinatesZ = z.object({
    latitude: z.string(),
    longitude: z.string(),
  });

  public static validate(values: unknown): CoordinatesParams {
    return parseOrThrow(CoordinatesSchema.CoordinatesZ, values, {
      logError: true,
      schemaName: "CoordinatesSchema",
      operation: "Validating CoordinatesParams",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): CoordinatesJSON {
    return parseOrThrow(CoordinatesSchema.CoordinatesJSONZ, json, {
      logError: true,
      schemaName: "CoordinatesSchema",
      operation: "Validating CoordinatesJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is CoordinatesParams {
    try {
      CoordinatesSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is CoordinatesJSON {
    try {
      CoordinatesSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: CoordinatesJSON): CoordinatesParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(CoordinatesSchema.CoordinatesJSONZ, json, {
      logError: true,
      schemaName: "CoordinatesSchema",
      operation: "deserialize CoordinatesJSON",
      payloadSample: json,
    });
    return CoordinatesSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: CoordinatesParams): CoordinatesJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = CoordinatesSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return CoordinatesSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(CoordinatesSchema.CoordinatesJSONZ, CoordinatesSchema.CoordinatesZ, {
    decode: (data) => ({
      latitude: data.latitude,
      longitude: data.longitude,
    }),
    encode: (coords) => ({
      latitude: coords.latitude,
      longitude: coords.longitude,
    }),
  });
}
